import { Box, Button, Grid, Input, Modal, IconButton, Snackbar, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import React from 'react';
import { useSelector } from 'react-redux';
import { FixedSizeList as List } from 'react-window';
import { LineupApiResponse } from '../../preContest/queries/queryTypes';
import RoiBoostButton from './RoiBoostButton';
import { useLocalStorage } from 'usehooks-ts';
import { GridApi, GridValidRowModel, gridFilteredSortedRowEntriesSelector } from '@mui/x-data-grid-pro';
import { showdownPositions } from '../../utils/showdownHandler';
import { isTeamSport } from '../../utils/teamSportHelper';
import { findLineupsToFavorite, getLineupPlayerKeys, isSuperflexSlot, superflexEligiblePositions, superflexKeySuffix } from './utils';
import { SlateResponse } from '../store/types';
import { IRootState } from '../../../store/reducer';

// Reusable Components
type SortDirection = 'asc' | 'desc' | null;

const HeaderCell = ({
  children,
  center = false,
  sortable = false,
  sortDirection = null,
  onSort,
}: {
  children: React.ReactNode;
  center?: boolean;
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: () => void;
}) => (
  <Box
    onClick={sortable ? onSort : undefined}
    sx={{
      fontSize: 11,
      fontWeight: 700,
      color: 'rgba(255, 255, 255, 0.70)',
      textTransform: 'uppercase',
      padding: '0 8px',
      textAlign: center ? 'center' : 'left',
      cursor: sortable ? 'pointer' : 'default',
      display: 'flex',
      alignItems: 'center',
      justifyContent: center ? 'center' : 'flex-start',
      gap: 0.5,
      '&:hover': sortable ? {
        color: 'rgba(255, 255, 255, 0.90)',
      } : {},
    }}
  >
    {children}
    {sortable && sortDirection === 'asc' && <ArrowUpwardIcon sx={{ fontSize: 14 }} />}
    {sortable && sortDirection === 'desc' && <ArrowDownwardIcon sx={{ fontSize: 14 }} />}
  </Box>
);

const DataCell = ({ children, center = false, bold = false }: { children: React.ReactNode; center?: boolean; bold?: boolean }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: center ? 'center' : 'flex-start',
    fontSize: 13,
    padding: '0 4px',
    fontWeight: bold ? 700 : 400,
    height: '100%',
  }}>
    {children}
  </Box>
);

const PositionPill = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <Box
    onClick={onClick}
    sx={{
      padding: '6px 12px',
      fontSize: 12,
      fontWeight: 500,
      cursor: 'pointer',
      borderRadius: 1.5,
      backgroundColor: active ? '#015ACE' : 'transparent',
      color: active ? 'white' : 'rgba(255, 255, 255, 0.7)',
      '&:hover': {
        backgroundColor: active ? '#015ACE' : 'rgba(1, 90, 206, 0.3)',
      },
    }}
  >
    {children}
  </Box>
);

interface OwnProps {
  readonly open: boolean;
  readonly players: LineupApiResponse[];
  readonly playerRoiList: { [key: string]: number };
  readonly playerExposureList: { [key: string]: number };
  readonly stackRoiList?: { [key: string]: number };
  readonly stackExposureList?: { [key: string]: number };
  readonly setPlayerRoiList: (list: { [key: string]: number }) => void;
  readonly setPlayerExposureList: (list: { [key: string]: number }) => void;
  readonly closeModal: () => void;
  readonly selectedSlate: SlateResponse;
  readonly onAdjustFavorites: () => void;
  readonly apiRef: React.MutableRefObject<GridApi>;
  readonly allLineups?: LineupApiResponse[];
}

const PlayerManagementModal = (props: OwnProps) => {
  const {
    open,
    players,
    playerRoiList,
    playerExposureList,
    stackRoiList = {},
    stackExposureList = {},
    setPlayerRoiList,
    setPlayerExposureList,
    closeModal,
    selectedSlate,
    onAdjustFavorites,
    apiRef,
    allLineups = [],
  } = props;

  const sport = selectedSlate?.sport;
  const slateType = selectedSlate?.type;
  const teamSport = isTeamSport(sport);
  const showdown = slateType?.toUpperCase() === 'SHOWDOWN';

  // Get unique limit from Redux state (set by QuickFavorite)
  const { uniqueLimit } = useSelector((state: IRootState) => state.common);

  const [updatedRoiList, setUpdatedRoiList] = React.useState<{ [key: string]: number }>(playerRoiList);
  const [updatedExposureList, setUpdatedExposureList] = React.useState<{ [key: string]: number }>(playerExposureList);
  const [filteredPlayers, setFilteredPlayers] = React.useState<LineupApiResponse[]>([]);
  const [searchVal, setSearchVal] = React.useState('');
  const [selectedPosition, setSelectedPosition] = React.useState<string>('');
  const [mlbPlayerType, setMlbPlayerType] = React.useState<'all' | 'batters' | 'pitchers'>('all');
  const [globalMaxExposure, setGlobalMaxExposure] = React.useState<number>(100);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [sortColumn, setSortColumn] = React.useState<string>('exposure');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');

  const nf = new Intl.NumberFormat('en-US');

  const [savedLineups, setSavedLineups] = useLocalStorage<(LineupApiResponse | GridValidRowModel)[]>(
    `precontest-lineups-${selectedSlate.slateId}`,
    []
  );

  // On the S-Flex tab, exposure, boosts and caps apply only when the player fills the superflex slot
  const superflexTab = isSuperflexSlot(selectedPosition);

  const getBoostKey = React.useCallback((player: string, position: string) => {
    if (superflexTab) return player + superflexKeySuffix;
    const positionCaps = position?.toUpperCase();
    return showdownPositions.includes(positionCaps) ? player + '||' + positionCaps : player;
  }, [superflexTab]);

  const getPlayerKey = React.useCallback((player: LineupApiResponse) => getBoostKey(player.exportableNameAndId, player.position), [getBoostKey]);

  // Calculate current exposure for each player based on savedLineups
  const computedExposures = React.useMemo(() => {
    const exposureMap: { [key: string]: number } = {};
    if (savedLineups.length === 0) return exposureMap;

    savedLineups.forEach(lineup => {
      if (!lineup?.exportableLineup) return;
      getLineupPlayerKeys(lineup.exportableLineup, selectedSlate).forEach(key => {
        exposureMap[key] = (exposureMap[key] || 0) + 1;
      });
    });

    // Convert counts to percentages
    const totalLineups = savedLineups.length;
    Object.keys(exposureMap).forEach(key => {
      exposureMap[key] = (exposureMap[key] / totalLineups) * 100;
    });

    return exposureMap;
  }, [savedLineups, selectedSlate]);

  // Get computed exposure for a player
  const getComputedExposure = React.useCallback((player: LineupApiResponse) => {
    const key = getPlayerKey(player);
    return computedExposures[key] || 0;
  }, [computedExposures, getPlayerKey]);

  const sortPlayers = React.useCallback((playerList: LineupApiResponse[]) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    return [...playerList].sort((a, b) => {
      switch (sortColumn) {
        case 'player':
          return multiplier * (a.player || '').localeCompare(b.player || '');
        case 'position':
          return multiplier * (a.position || '').localeCompare(b.position || '');
        case 'team':
          return multiplier * (a.teamName || '').localeCompare(b.teamName || '');
        case 'salary':
          return multiplier * ((parseInt(a.salary) || 0) - (parseInt(b.salary) || 0));
        case 'projection':
          return multiplier * ((a.projection || 0) - (b.projection || 0));
        case 'ownership':
          return multiplier * (parseFloat(a.projectedOwnershipPct || '0') - parseFloat(b.projectedOwnershipPct || '0'));
        case 'poolOwnership':
          return multiplier * (parseFloat(a.overallOwnershipPct || '0') - parseFloat(b.overallOwnershipPct || '0'));
        case 'exposure':
          return multiplier * (getComputedExposure(a) - getComputedExposure(b));
        case 'roiBoost':
          return multiplier * ((updatedRoiList[getPlayerKey(a)] || 0) - (updatedRoiList[getPlayerKey(b)] || 0));
        case 'exposureCap':
          return multiplier * ((updatedExposureList[getPlayerKey(a)] ?? 100) - (updatedExposureList[getPlayerKey(b)] ?? 100));
        default:
          return multiplier * (getComputedExposure(a) - getComputedExposure(b));
      }
    });
  }, [sortColumn, sortDirection, updatedRoiList, updatedExposureList, getPlayerKey, getComputedExposure]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  React.useEffect(() => {
    setFilteredPlayers(sortPlayers(players));
  }, [players, sortPlayers]);

  React.useEffect(() => {
    setUpdatedRoiList(playerRoiList);
  }, [playerRoiList]);

  React.useEffect(() => {
    setUpdatedExposureList(playerExposureList);
  }, [playerExposureList]);

  React.useEffect(() => {
    let result = players;
    if (searchVal) {
      result = result.filter(x => x.player?.toLowerCase().includes(searchVal.toLowerCase()));
    }
    if (isSuperflexSlot(selectedPosition)) {
      // S-Flex is a slot, not a position: list every player who can fill it
      result = result.filter(x => superflexEligiblePositions.includes(x.position?.toUpperCase()));
    } else if (selectedPosition) {
      result = result.filter(x => x.position?.toLowerCase().includes(selectedPosition.toLowerCase()));
    }
    // MLB-specific filter for batters vs pitchers
    if (sport?.toLowerCase() === 'mlb' && mlbPlayerType !== 'all') {
      if (mlbPlayerType === 'pitchers') {
        result = result.filter(x => x.position?.toUpperCase().includes('P'));
      } else {
        // Batters - exclude pitchers
        result = result.filter(x => !x.position?.toUpperCase().includes('P'));
      }
    }
    setFilteredPlayers(sortPlayers(result));
  }, [searchVal, selectedPosition, mlbPlayerType, players, sortPlayers, sport]);

  const handleRoiChange = React.useCallback((player: string, position: string, percent: number) => {
    const key = getBoostKey(player, position);
    setUpdatedRoiList(prev => ({ ...prev, [key]: percent }));
  }, [getBoostKey]);

  const handleExposureChange = React.useCallback((player: string, position: string, percent: number) => {
    const key = getBoostKey(player, position);
    setUpdatedExposureList(prev => ({ ...prev, [key]: percent }));
  }, [getBoostKey]);

  const handleGlobalMaxExposureChange = (percent: number) => {
    setGlobalMaxExposure(percent);
    // Store global exposure as a separate key, don't overwrite individual player exposures
    setUpdatedExposureList(prev => ({ ...prev, global: percent }));
  };

  const getRoiPercent = React.useCallback((player: string, position: string) => {
    const key = getBoostKey(player, position);
    return updatedRoiList[key] !== undefined ? updatedRoiList[key] : 0;
  }, [updatedRoiList, getBoostKey]);

  const getExposurePercent = React.useCallback((player: string, position: string) => {
    const key = getBoostKey(player, position);
    return updatedExposureList[key] !== undefined ? updatedExposureList[key] : 100;
  }, [updatedExposureList, getBoostKey]);

  const handleClearExposures = () => {
    setUpdatedExposureList({});
    setGlobalMaxExposure(100); // Reset global max exposure to default
  };
  const handleClearBoosts = () => setUpdatedRoiList({});

  // Calculate player ROI boost for a lineup based on individual player boosts
  const getPlayerRoiBoost = (lineup: LineupApiResponse, playerBoostList: { [key: string]: number }): number => {
    if (!lineup?.exportableLineup) return 0;
    let totalBoost = 0;
    getLineupPlayerKeys(lineup.exportableLineup, selectedSlate).forEach(key => {
      const boost = playerBoostList[key];
      if (boost !== undefined && boost !== 0) {
        totalBoost += boost / 100; // Convert percentage to decimal
      }
    });
    return totalBoost;
  };

  // Calculate stack ROI boost for a lineup
  const getStackRoiBoost = (lineup: LineupApiResponse, stackBoostList: { [key: string]: number }): number => {
    if (!lineup?.stacks) return 0;
    let totalBoost = 0;
    lineup.stacks.forEach(stack => {
      if ((selectedSlate?.sport?.toLowerCase() === 'mlb' && stack.count > 2) ||
          (selectedSlate?.sport?.toLowerCase() === 'nfl' && stack.count > 0)) {
        const boost = stackBoostList[stack.team];
        if (boost !== undefined && boost !== 0) {
          totalBoost += boost / 100;
        }
      }
    });
    return totalBoost;
  };

  const handleAdjustFavorites = () => {
    const filteredRoiList: { [key: string]: number } = {};
    const filteredExposureList: { [key: string]: number } = {};

    Object.keys(updatedRoiList).forEach(key => {
      if (updatedRoiList[key] !== 0) {
        filteredRoiList[key] = updatedRoiList[key];
      }
    });

    Object.keys(updatedExposureList).forEach(key => {
      if (updatedExposureList[key] !== 100 && updatedExposureList[key] !== undefined) {
        filteredExposureList[key] = updatedExposureList[key];
      }
    });

    setPlayerRoiList(filteredRoiList);
    setPlayerExposureList(filteredExposureList);

    // Show snackbar immediately
    setSnackbarOpen(true);

    // Call onAdjustFavorites to trigger ROI recalculation
    onAdjustFavorites();

    // Only try to refavorite if we have saved lineups
    if (savedLineups.length === 0) return;

    const lineupCount = savedLineups.length;

    // Prioritize using allLineups if available - this works regardless of which tab is showing
    // (handles exposure tab case where grid shows player data, not lineup data)
    if (allLineups.length > 0) {
      // Use setTimeout to ensure parent state updates are applied before we recalculate
      // Don't clear savedLineups first to avoid visual flash
      setTimeout(() => {
        // Get filtered lineups from the grid if filters are applied
        let filteredLineupSet: Set<string> | null = null;
        try {
          if (apiRef?.current) {
            const filteredRows = gridFilteredSortedRowEntriesSelector(apiRef);
            // Check if the grid contains lineup data (has exportableLineup)
            if (filteredRows.length > 0 && filteredRows[0]?.model?.exportableLineup) {
              filteredLineupSet = new Set(filteredRows.map(row => row.model.exportableLineup));
            }
          }
        } catch (e) {
          // Grid not available or error - proceed without filtering
        }

        // Grid doesn't have lineup data (e.g., exposure tab) - use allLineups array directly
        // Apply boosts and sort by effective simLineupRoi
        // If we have a filtered set, only include lineups that match the filter
        const lineupsWithBoosts = allLineups
          .filter(l => l.exportableLineup && l.exportableLineup.length > 0)
          .filter(l => !filteredLineupSet || filteredLineupSet.has(l.exportableLineup))
          .map(lineup => {
            const playerBoost = getPlayerRoiBoost(lineup, filteredRoiList);
            const stackBoost = getStackRoiBoost(lineup, stackRoiList);
            const effectiveRoi = (lineup.simLineupRoi || 0) + playerBoost + stackBoost;
            return { ...lineup, effectiveRoi };
          })
          .sort((a, b) => b.effectiveRoi - a.effectiveRoi);

        // Select lineups respecting exposure limits and uniqueness
        let currentPlayerExposure: { [key: string]: number } = {};
        let currentStackExposure: { [key: string]: number } = {};
        const globalPlayerExposure = filteredExposureList['global'] || 100;
        const globalStackExposure = stackExposureList['global'] || 100;
        let favoritedLineups: LineupApiResponse[] = [];
        let favoritedLineupsStrings: string[] = [];

        // Helper function to check uniqueness
        const hasCaptain = selectedSlate?.type?.toUpperCase() === 'SHOWDOWN';
        const checkUniqueness = (lineup: LineupApiResponse, existingLineups: string[]): boolean => {
          if (!uniqueLimit || uniqueLimit === 0) return true;

          let players = lineup.exportableLineup.split(',');
          if (hasCaptain) {
            players = players.map((x, i) => i === 0 ? `${x}||CPT` : x);
          }

          return !existingLineups.some(existingLineup => {
            let existingPlayers = existingLineup.split(',');
            if (hasCaptain) {
              existingPlayers = existingPlayers.map((x, i) => i === 0 ? `${x}||CPT` : x);
            }
            const uniquePlayers = players.filter(player => !existingPlayers.includes(player));
            return uniquePlayers.length < uniqueLimit;
          });
        };

        for (const lineup of lineupsWithBoosts) {
          if (favoritedLineups.length >= lineupCount) break;

          const lineupKeys = getLineupPlayerKeys(lineup.exportableLineup || '', selectedSlate);
          let canAdd = true;

          // Check uniqueness constraint first
          if (!checkUniqueness(lineup, favoritedLineupsStrings)) {
            canAdd = false;
          }

          // Check player exposure limits
          // Calculate max allowed count based on target lineup count (not current count)
          // E.g., if we want 150 lineups and max exposure is 50%, player can be in at most 75 lineups
          // Use Math.min to ensure global cap overrides higher individual caps
          if (canAdd) {
            for (const key of lineupKeys) {
              const wouldBeCount = (currentPlayerExposure[key] || 0) + 1;
              const maxExpPct = Math.min(filteredExposureList[key] ?? 100, globalPlayerExposure);
              const maxAllowedCount = Math.ceil(lineupCount * (maxExpPct / 100));
              if (wouldBeCount > maxAllowedCount) {
                canAdd = false;
                break;
              }
            }
          }

          // Check stack exposure limits
          if (canAdd && lineup.stacks) {
            for (const stack of lineup.stacks) {
              if ((selectedSlate?.sport?.toLowerCase() === 'mlb' && stack.count > 2) ||
                  (selectedSlate?.sport?.toLowerCase() === 'nfl' && stack.count > 0)) {
                const wouldBeCount = (currentStackExposure[stack.team] || 0) + 1;
                const maxExpPct = Math.min(stackExposureList[stack.team] ?? 100, globalStackExposure);
                const maxAllowedCount = Math.ceil(lineupCount * (maxExpPct / 100));
                if (wouldBeCount > maxAllowedCount) {
                  canAdd = false;
                  break;
                }
              }
            }
          }

          if (canAdd) {
            favoritedLineups.push(lineup);
            favoritedLineupsStrings.push(lineup.exportableLineup);
            // Update exposure counts
            for (const key of lineupKeys) {
              currentPlayerExposure[key] = (currentPlayerExposure[key] || 0) + 1;
            }
            if (lineup.stacks) {
              for (const stack of lineup.stacks) {
                if ((selectedSlate?.sport?.toLowerCase() === 'mlb' && stack.count > 2) ||
                    (selectedSlate?.sport?.toLowerCase() === 'nfl' && stack.count > 0)) {
                  currentStackExposure[stack.team] = (currentStackExposure[stack.team] || 0) + 1;
                }
              }
            }
          }
        }

        setSavedLineups(favoritedLineups);
      }, 100);
    } else {
      // Fallback: Try to use grid API if allLineups is not available
      // Check if the grid has lineup data (not player exposure data)
      let hasLineupData = false;
      try {
        if (apiRef?.current && typeof apiRef.current.getRowModels === 'function') {
          const rowModels = apiRef.current.getRowModels();
          if (rowModels.size > 0) {
            const firstRow = rowModels.values().next().value;
            hasLineupData = !!(firstRow?.exportableLineup && firstRow.exportableLineup.length > 0);
          }
        }
      } catch (e) {
        // Grid not available
      }

      if (hasLineupData) {
        // Grid has lineup data - use grid API for refavoriting
        // Don't clear savedLineups first to avoid visual flash
        setTimeout(() => {
          try {
            apiRef.current.applySorting();
            setTimeout(() => {
              try {
                const newFavoritedLineups = findLineupsToFavorite(
                  filteredExposureList,
                  stackExposureList,
                  [],
                  apiRef,
                  selectedSlate,
                  lineupCount,
                  uniqueLimit ? String(uniqueLimit) : undefined
                );
                setSavedLineups(newFavoritedLineups);
              } catch (e) {
                console.warn('Could not refavorite lineups:', e);
              }
            }, 100);
          } catch (e) {
            console.warn('Could not apply sorting:', e);
          }
        }, 100);
      }
    }
  };

  const getPositions = () => {
    const { site, sport, type } = selectedSlate;

    if (
      site?.toLowerCase() === 'fd' &&
      ['nba', 'mlb', 'nfl'].indexOf(sport?.toLowerCase()) >= 0 &&
      type?.toLowerCase() === 'showdown'
    ) {
      return ['CPT', 'FLEX'];
    } else if (
      site?.toLowerCase() === 'dk' &&
      ['nba'].indexOf(sport?.toLowerCase()) >= 0 &&
      type?.toLowerCase() === 'showdown'
    ) {
      return ['CPT', 'FLEX'];
    }

    // Get positions from lineup rules
    const lineupPositions = selectedSlate?.lineupRules?.positions
      ?.split(',')
      .filter((value, index, self) => self.indexOf(value) === index) || [];

    // Filter out flex/utility positions that don't map to actual player positions
    // These are lineup slots, not player positions, so filtering by them returns no results
    // On showdown slates FLEX is a real player position (rows are tagged CPT/FLEX), so keep it as a tab
    const flexPositions = type?.toLowerCase() === 'showdown' ? ['UTIL', 'G', 'F'] : ['UTIL', 'G', 'F', 'FLEX', 'G/F', 'F/C'];
    const filteredPositions = lineupPositions.filter(pos => !flexPositions.includes(pos.toUpperCase()));

    // Map lineup rule positions to actual player positions for filtering (only for FanDuel)
    if (site?.toLowerCase() === 'fd') {
      const positionMapping: { [key: string]: string } = { 'DEF': 'D' };
      return filteredPositions.map(pos => positionMapping[pos] || pos);
    }

    return filteredPositions;
  };

  const getPlayerColumnWidth = () => {
    if (teamSport) return 2;
    if (showdown) return 3;
    return 4;
  };

  const ROW_HEIGHT = 64;
  const LIST_HEIGHT = 300;

  // Memoized row renderer for virtualization
  const PlayerRow = React.memo(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const player = filteredPlayers[index];
    if (!player) return null;

    return (
      <Box
        style={style}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.02)' },
        }}
      >
        <Grid container alignItems="center" sx={{ height: '100%' }}>
          <Grid item xs={getPlayerColumnWidth()}>
            <DataCell>
              <Box>
                <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.70)', fontSize: 12, textTransform: 'uppercase' }}>
                  {player.player?.split(' ')[0]}
                </Box>{' '}
                <Box component="span" sx={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase' }}>
                  {player.player?.split(' ')?.slice(1).join(' ')}
                </Box>
              </Box>
            </DataCell>
          </Grid>
          {(teamSport || showdown) && <Grid item xs={1}><DataCell>{player.position}</DataCell></Grid>}
          {teamSport && <Grid item xs={1}><DataCell>{player.teamName || '-'}</DataCell></Grid>}
          <Grid item xs={1}><DataCell>{`$${nf.format(parseInt(player.salary))}`}</DataCell></Grid>
          <Grid item xs={1}><DataCell>{player.projection?.toFixed(2) || '-'}</DataCell></Grid>
          <Grid item xs={1}><DataCell>{player.projectedOwnershipPct || '-'}</DataCell></Grid>
          <Grid item xs={1}><DataCell>{player.overallOwnershipPct || '-'}</DataCell></Grid>
          <Grid item xs={1}><DataCell center bold>{`${getComputedExposure(player).toFixed(2)}%`}</DataCell></Grid>
          <Grid item xs={1.5}>
            <DataCell>
              <RoiBoostButton
                defaultPercent={0}
                onChange={(percent) => handleRoiChange(player.exportableNameAndId, player.position, percent)}
                previousPercent={getRoiPercent(player.exportableNameAndId, player.position)}
              />
            </DataCell>
          </Grid>
          <Grid item xs={1.5}>
            <DataCell>
              <RoiBoostButton
                defaultPercent={100}
                onChange={(percent) => handleExposureChange(player.exportableNameAndId, player.position, percent)}
                previousPercent={getExposurePercent(player.exportableNameAndId, player.position)}
                constantMax={100}
                constantMin={0}
              />
            </DataCell>
          </Grid>
        </Grid>
      </Box>
    );
  });

  return (
    <>
      <Modal
        open={open}
        disableAutoFocus
        onClose={(_event: object, reason: string) => {
          if (reason?.toLowerCase() === 'backdropclick') closeModal();
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontFamily: 'Roboto',
          backgroundColor: 'rgba(125, 125, 125, .65)',
        }}
      >
        <Box sx={{
        backgroundColor: '#0A0A0A',
        borderRadius: 2,
        minHeight: 400,
        width: 1050,
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, .30)',
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 20px 0 20px' }}>
          <Box>
            <Box sx={{ fontSize: 20, fontWeight: 600, marginBottom: 1 }}>Player Management</Box>
            <Box sx={{ fontSize: 14, fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)', maxWidth: 600, lineHeight: 1.4 }}>
              Add positive or negative ROI boosts to lineups that include this player to influence ROI rankings.
              You can also set exposure caps to limit how often the player appears.
            </Box>
          </Box>
          <IconButton onClick={closeModal} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Search */}
        <Box sx={{ margin: '16px 20px', backgroundColor: 'rgba(43, 48, 62, 0.15)', border: '1px solid #2C3140', borderRadius: 1 }}>
          <Input
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            placeholder="Search Players"
            disableUnderline
            endAdornment={<SearchIcon style={{ color: 'rgba(255,255,255,0.5)' }} />}
            sx={{ color: 'rgba(255, 255, 255, 0.70)', width: '100%', padding: '8px 12px' }}
          />
        </Box>

        {/* Position Filters */}
        <Box sx={{ display: 'flex', gap: 1, padding: '0 20px 16px 20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <PositionPill active={!selectedPosition} onClick={() => setSelectedPosition('')}>ALL</PositionPill>
          {getPositions().map((position) => (
            <PositionPill key={position} active={selectedPosition === position} onClick={() => setSelectedPosition(position)}>
              {position}
            </PositionPill>
          ))}
          {/* MLB Batters/Pitchers Toggle */}
          {sport?.toLowerCase() === 'mlb' && (
            <>
              <Box sx={{ width: 1, height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.2)', mx: 1 }} />
              <PositionPill active={mlbPlayerType === 'all'} onClick={() => setMlbPlayerType('all')}>All Players</PositionPill>
              <PositionPill active={mlbPlayerType === 'batters'} onClick={() => setMlbPlayerType('batters')}>Batters</PositionPill>
              <PositionPill active={mlbPlayerType === 'pitchers'} onClick={() => setMlbPlayerType('pitchers')}>Pitchers</PositionPill>
            </>
          )}
        </Box>

        {/* Table Header */}
        <Box sx={{ backgroundColor: 'rgba(1, 90, 206, 0.15)', borderBottom: '1px solid rgba(1, 90, 206, .8)', padding: '8px 0', margin: '0 20px' }}>
          <Grid container alignItems="center">
            <Grid item xs={getPlayerColumnWidth()}>
              <HeaderCell sortable sortDirection={sortColumn === 'player' ? sortDirection : null} onSort={() => handleSort('player')}>Player</HeaderCell>
            </Grid>
            {(teamSport || showdown) && <Grid item xs={1}>
              <HeaderCell sortable sortDirection={sortColumn === 'position' ? sortDirection : null} onSort={() => handleSort('position')}>Pos.</HeaderCell>
            </Grid>}
            {teamSport && <Grid item xs={1}>
              <HeaderCell sortable sortDirection={sortColumn === 'team' ? sortDirection : null} onSort={() => handleSort('team')}>Team</HeaderCell>
            </Grid>}
            <Grid item xs={1}>
              <HeaderCell sortable sortDirection={sortColumn === 'salary' ? sortDirection : null} onSort={() => handleSort('salary')}>Salary</HeaderCell>
            </Grid>
            <Grid item xs={1}>
              <HeaderCell sortable sortDirection={sortColumn === 'projection' ? sortDirection : null} onSort={() => handleSort('projection')}>Proj.</HeaderCell>
            </Grid>
            <Grid item xs={1}>
              <HeaderCell sortable sortDirection={sortColumn === 'ownership' ? sortDirection : null} onSort={() => handleSort('ownership')}>Own. %</HeaderCell>
            </Grid>
            <Grid item xs={1}>
              <HeaderCell sortable sortDirection={sortColumn === 'poolOwnership' ? sortDirection : null} onSort={() => handleSort('poolOwnership')}>Pool Own</HeaderCell>
            </Grid>
            <Grid item xs={1}>
              <HeaderCell center sortable sortDirection={sortColumn === 'exposure' ? sortDirection : null} onSort={() => handleSort('exposure')}>Current Exposure</HeaderCell>
            </Grid>
            <Grid item xs={1.5}>
              <HeaderCell center sortable sortDirection={sortColumn === 'roiBoost' ? sortDirection : null} onSort={() => handleSort('roiBoost')}>ROI Boost</HeaderCell>
            </Grid>
            <Grid item xs={1.5}>
              <HeaderCell center sortable sortDirection={sortColumn === 'exposureCap' ? sortDirection : null} onSort={() => handleSort('exposureCap')}>Exposure Cap</HeaderCell>
            </Grid>
          </Grid>
        </Box>

        {/* Virtualized Table Body */}
        <Box sx={{ margin: '0 20px', overflow: 'hidden' }}>
          <List
            height={LIST_HEIGHT}
            itemCount={filteredPlayers.length}
            itemSize={ROW_HEIGHT}
            width="100%"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'slategrey transparent',
              overflowX: 'hidden',
            }}
          >
            {PlayerRow}
          </List>
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, padding: '16px 20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', backgroundColor: '#0A0A0A' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14 }}>
            <span>Global Max Exposure:</span>
            <RoiBoostButton
              defaultPercent={100}
              onChange={handleGlobalMaxExposureChange}
              previousPercent={globalMaxExposure}
              constantMax={100}
              constantMin={0}
            />
          </Box>
          <Button
            variant="outlined"
            onClick={handleClearExposures}
            sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)', '&:hover': { borderColor: 'rgba(255, 255, 255, 0.5)', backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}
          >
            CLEAR EXPOSURES
          </Button>
          <Button
            variant="outlined"
            onClick={handleClearBoosts}
            sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)', '&:hover': { borderColor: 'rgba(255, 255, 255, 0.5)', backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}
          >
            CLEAR BOOSTS
          </Button>
          <Button
            variant="contained"
            endIcon={<OpenInNewIcon />}
            onClick={handleAdjustFavorites}
            sx={{ backgroundColor: '#015ACE', color: 'white', '&:hover': { backgroundColor: '#0148a8' } }}
          >
            ADJUST FAVORITES
          </Button>
        </Box>
      </Box>
    </Modal>
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={3000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
        Favorites updated successfully!
      </Alert>
    </Snackbar>
    </>
  );
};

export default PlayerManagementModal;
